/**
 * Heuristic transaction-level risk for a single payout (cash mix, amount bands,
 * tier, win type). Complements patron/profile risk from the server (riskScore).
 *
 * Knobs come from venue_configuration `transaction_risk.*` (group scope) via
 * GET /api/venue-config/transaction-risk. The DEFAULT_TXN_RISK_CONFIG below
 * mirrors VenueConfigService.DEFAULTS so behaviour is identical to the previous
 * hard-coded heuristic when the API is unreachable or the config row is unset.
 */

const CAP = 100

export const DEFAULT_TXN_RISK_CONFIG = Object.freeze({
  amount_band_low_threshold: 2000, amount_band_low_points: 10,
  amount_band_mid_threshold: 5000, amount_band_mid_points: 22,
  amount_band_high_threshold: 10000, amount_band_high_points: 26,
  cash_ratio_low_threshold: 50, cash_ratio_low_points: 6,
  cash_ratio_mid_threshold: 70, cash_ratio_mid_points: 12,
  cash_ratio_high_threshold: 90, cash_ratio_high_points: 18,
  cash_component_cap: 5000, cash_component_cap_points: 16,
  cash_component_near_cap: 4500, cash_component_near_cap_points: 8,
  tier_pv3_points: 6, tier_pv4_points: 10,
  win_type_jackpot_points: 8,
  level_high_threshold: 70, level_medium_threshold: 40,
  investigate_threshold: 50,
  patron_high_combined_patron_score: 70,  // patron risk score ≥70 = HIGH on traditional scale
  patron_high_combined_txn_score: 35,
})

function pick(config, key) {
  const v = config?.[key]
  return Number.isFinite(v) ? v : DEFAULT_TXN_RISK_CONFIG[key]
}

function addFactor(factors, code, label, detail, pts) {
  if (pts <= 0) return 0
  factors.push({ code, label, detail, contribution: pts })
  return pts
}

/**
 * @param {object} payout - row from /api/monitoring/payouts
 * @param {object|null} split - from effectivePayoutSplit: { cash, eft, cheque, source? }
 * @param {object} [config] - resolved transaction_risk.* knobs; defaults applied per-field
 * @returns {{ score: number, level: string, factors: Array, suggestInvestigate: boolean, summary: string }}
 */
export function computeTransactionRisk(payout, split, config) {
  const factors = []
  let score = 0
  const cfg = config || DEFAULT_TXN_RISK_CONFIG

  const amount = payout?.amount != null ? Number(payout.amount) : NaN
  if (Number.isFinite(amount) && amount > 0) {
    const hi = pick(cfg, 'amount_band_high_threshold')
    const mid = pick(cfg, 'amount_band_mid_threshold')
    const lo = pick(cfg, 'amount_band_low_threshold')
    if (amount >= hi) {
      score += addFactor(factors, 'amt_high', 'Amount', `Total payout at or above $${hi.toLocaleString()}`, pick(cfg, 'amount_band_high_points'))
    } else if (amount >= mid) {
      score += addFactor(factors, 'amt_mid', 'Amount', `Total payout at or above $${mid.toLocaleString()} (threshold band)`, pick(cfg, 'amount_band_mid_points'))
    } else if (amount >= lo) {
      score += addFactor(factors, 'amt_low', 'Amount', `Total payout $${lo.toLocaleString()}–$${(mid - 1).toLocaleString()}`, pick(cfg, 'amount_band_low_points'))
    }
  }

  const cashPct = payout?.cashRatioPct != null ? Number(payout.cashRatioPct) : null
  if (Number.isFinite(cashPct)) {
    const hi = pick(cfg, 'cash_ratio_high_threshold')
    const mid = pick(cfg, 'cash_ratio_mid_threshold')
    const lo = pick(cfg, 'cash_ratio_low_threshold')
    if (cashPct >= hi) {
      score += addFactor(factors, 'cash_high', 'Cash ratio', `≥${hi}% cash component of payout`, pick(cfg, 'cash_ratio_high_points'))
    } else if (cashPct >= mid) {
      score += addFactor(factors, 'cash_mid', 'Cash ratio', `≥${mid}% cash component`, pick(cfg, 'cash_ratio_mid_points'))
    } else if (cashPct >= lo) {
      score += addFactor(factors, 'cash_low', 'Cash ratio', `≥${lo}% cash component`, pick(cfg, 'cash_ratio_low_points'))
    }
  }

  const cashAmt = split && Number.isFinite(split.cash) ? split.cash : 0
  const cap = pick(cfg, 'cash_component_cap')
  const nearCap = pick(cfg, 'cash_component_near_cap')
  if (cashAmt >= cap) {
    score += addFactor(
      factors,
      'cash_desk',
      'Cash component',
      `Cash portion meets or exceeds desk cap ($${cap.toLocaleString()}) — structuring or split review`,
      pick(cfg, 'cash_component_cap_points'),
    )
  } else if (cashAmt > 0 && Number.isFinite(amount) && amount > cap && cashAmt >= nearCap) {
    score += addFactor(factors, 'cash_near_cap', 'Cash component', 'High cash near desk cap on a large total payout', pick(cfg, 'cash_component_near_cap_points'))
  }

  const tier = String(payout?.payoutTier || '').toUpperCase()
  if (tier === 'PV4') {
    score += addFactor(factors, 'tier_pv4', 'Payout tier', 'PV4 ($10k+) — highest verification tier', pick(cfg, 'tier_pv4_points'))
  } else if (tier === 'PV3') {
    score += addFactor(factors, 'tier_pv3', 'Payout tier', 'PV3 ($5k–$10k)', pick(cfg, 'tier_pv3_points'))
  }

  const gt = String(payout?.gameType || '').toLowerCase()
  if (/jackpot|link\s*jackpot|major|grand|progressive/.test(gt)) {
    score += addFactor(factors, 'win_type', 'Type of win', 'Jackpot / linked / major prize wording in game type', pick(cfg, 'win_type_jackpot_points'))
  }

  if (
    payout?.patronNameMismatch ||
    (payout?.enrichmentFlags || []).some((f) =>
      ['patron_profile_name_mismatch', 'dim_patron_name_mismatch'].includes(f.code)
    )
  ) {
    const detail =
      payout?.patronNameMismatchNote ||
      (payout?.enrichmentFlags || []).find((f) =>
        ['patron_profile_name_mismatch', 'dim_patron_name_mismatch'].includes(f.code)
      )?.message ||
      'Payout capture name differs from linked patron profile'
    score += addFactor(factors, 'name_mismatch', 'Name mismatch', detail, 20)
  }

  score = Math.min(CAP, Math.round(score))
  const levelHigh = pick(cfg, 'level_high_threshold')
  const levelMed = pick(cfg, 'level_medium_threshold')
  const level = score >= levelHigh ? 'HIGH' : score >= levelMed ? 'MEDIUM' : 'LOW'

  const patronHi = Number(payout?.riskScore) >= pick(cfg, 'patron_high_combined_patron_score')
  const amtThreshold = Number.isFinite(amount) && amount >= pick(cfg, 'amount_band_mid_threshold')
  const nameMismatch =
    payout?.patronNameMismatch ||
    (payout?.enrichmentFlags || []).some((f) =>
      ['patron_profile_name_mismatch', 'dim_patron_name_mismatch'].includes(f.code)
    )
  const suggestInvestigate =
    score >= pick(cfg, 'investigate_threshold')
    || amtThreshold
    || cashAmt >= cap
    || nameMismatch
    || (patronHi && score >= pick(cfg, 'patron_high_combined_txn_score'))

  const summary = `${level} transaction risk (${score}/100)${suggestInvestigate ? ' — review recommended' : ''}`

  return { score, level, factors, suggestInvestigate, summary }
}

export function transactionRiskLevelColor(level) {
  if (level === 'HIGH') return 'text-red-600'
  if (level === 'MEDIUM') return 'text-amber-600'
  return 'text-emerald-700'
}

export function transactionRiskBarClass(score) {
  if (score >= 70) return 'bg-red-600'
  if (score >= 40) return 'bg-amber-500'
  return 'bg-emerald-500'
}
